import {
  AutoComplete,
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import classNames from 'classnames/bind';

import { BottomButtons, HeadingH1 } from 'components/ui';
import { getAddress } from 'utils/AddressFinder';
import { isReclaimDateDisabled } from './reclaimDateRules';

import styles from './new.module.scss';

const cx = classNames.bind(styles);

const ReclaimForm = ({
  title,
  form,
  initialValues,
  locations,
  customerCompanyFieldType,
  customerCompanyOptions = [],
  customerNameFieldType,
  customerNameOptions = [],
  onCustomerCompanySearch,
  onCustomerNameSearch,
  onCustomerCompanyResolved,
  onCustomerNameResolved,
  isLoadingForm,
  isSubmitting,
  onSubmit,
  onBack,
}) => {
  const findAddress = async () => {
    const data = await getAddress();
    form.setFieldValue('customerAddress', data.address);
  };

  const createAutocompleteKeyDownHandler =
    (fieldName, options, onResolved) => (event) => {
      if (event.key !== 'Enter') {
        return;
      }

      // 한글 IME 조합 중 Enter는 글자 확정용으로 먼저 소비되므로
      // 자동완성 확정을 섞으면 마지막 글자가 중복 입력될 수 있다.
      if (event.nativeEvent?.isComposing || event.keyCode === 229) {
        return;
      }

      if ((options ?? []).length !== 1) {
        return;
      }

      const onlyOptionValue = options[0]?.value;
      const currentValue = form.getFieldValue(fieldName);

      if (!onlyOptionValue) {
        return;
      }

      if (currentValue !== onlyOptionValue) {
        event.preventDefault();
        form.setFieldValue(fieldName, onlyOptionValue);
      }

      onResolved?.(onlyOptionValue);
    };

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={title} />
        <Form
          layout="vertical"
          onFinish={onSubmit}
          form={form}
          className={cx('form')}
          initialValues={initialValues}
        >
          <Typography.Text className={cx('title')} strong>
            회수 정보
          </Typography.Text>
          <Row align={'top'} style={{ maxWidth: 840 }} justify={'space-between'}>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'reclaimDate'}
                label="회수 요청일"
                rules={[{ required: true, message: '회수 요청일을 선택해주세요' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={isLoadingForm || isSubmitting}
                  allowClear={false}
                  disabledDate={isReclaimDateDisabled}
                />
              </Form.Item>
              <Form.Item
                name={'customerCompany'}
                label="회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}
              >
                {customerCompanyFieldType === 'autocomplete' ? (
                  <AutoComplete
                    options={customerCompanyOptions}
                    onSearch={onCustomerCompanySearch}
                    onChange={onCustomerCompanySearch}
                    onSelect={onCustomerCompanyResolved}
                    filterOption={false}
                    disabled={isLoadingForm || isSubmitting}
                  >
                    <Input
                      disabled={isLoadingForm || isSubmitting}
                      onKeyDown={createAutocompleteKeyDownHandler(
                        'customerCompany',
                        customerCompanyOptions,
                        onCustomerCompanyResolved,
                      )}
                    />
                  </AutoComplete>
                ) : (
                  <Input disabled={isLoadingForm || isSubmitting} />
                )}
              </Form.Item>
              <Form.Item
                name={'customerName'}
                label="고객명"
                rules={[
                  {
                    required: true,
                    message:
                      customerNameFieldType === 'select'
                        ? '고객명을 선택해주세요'
                        : '고객명을 입력해주세요',
                  },
                ]}
              >
                {customerNameFieldType === 'select' ? (
                  <Select
                    placeholder="고객명 선택"
                    options={customerNameOptions}
                    disabled={isLoadingForm || isSubmitting}
                  />
                ) : customerNameFieldType === 'autocomplete' ? (
                  <AutoComplete
                    options={customerNameOptions}
                    onSearch={onCustomerNameSearch}
                    onChange={onCustomerNameSearch}
                    onSelect={onCustomerNameResolved}
                    filterOption={false}
                    disabled={isLoadingForm || isSubmitting}
                  >
                    <Input
                      disabled={isLoadingForm || isSubmitting}
                      onKeyDown={createAutocompleteKeyDownHandler(
                        'customerName',
                        customerNameOptions,
                        onCustomerNameResolved,
                      )}
                    />
                  </AutoComplete>
                ) : (
                  <Input disabled={isLoadingForm || isSubmitting} />
                )}
              </Form.Item>
              <Form.Item
                name={'customerAddress'}
                label="주소"
                rules={[{ required: true, message: '주소를 입력해주세요' }]}
              >
                <Input.Search
                  onClick={findAddress}
                  onSearch={findAddress}
                  loading={isLoadingForm || isSubmitting}
                  disabled={isLoadingForm || isSubmitting}
                />
              </Form.Item>
              <Form.Item name={'customerAddressDetail'} label="상세주소">
                <Input disabled={isLoadingForm || isSubmitting} />
              </Form.Item>
            </Space>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'quantity'}
                label="수량"
                rules={[{ required: true, message: '수량을 입력해주세요' }]}
              >
                <InputNumber style={{ width: '100%' }} disabled={isLoadingForm || isSubmitting} />
              </Form.Item>
              <Form.Item
                name={'reclaimLocationRid'}
                label="최종 거점"
                rules={[{ required: true, message: '최종 거점을 선택해주세요' }]}
              >
                <Select
                  options={locations}
                  placeholder="최종 거점 선택"
                  disabled={isLoadingForm || isSubmitting}
                />
              </Form.Item>
              <Form.Item
                name={'customerPhone'}
                label="전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}
              >
                <Input disabled={isLoadingForm || isSubmitting} />
              </Form.Item>
            </Space>
          </Row>

          <Divider />

          <Typography.Text className={cx('title')} strong>
            부가정보
          </Typography.Text>
          <Form.Item name={'memo'} label="메모">
            <Input.TextArea
              style={{ resize: 'none', maxWidth: 840, minHeight: 150 }}
              disabled={isLoadingForm || isSubmitting}
            />
          </Form.Item>

          <BottomButtons
            left={[
              <Button key={'backPage'} onClick={onBack}>
                뒤로
              </Button>,
            ]}
            right={[
              <Button
                key={'saveReclaim'}
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={isLoadingForm}
              >
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
    </div>
  );
};

export default ReclaimForm;
